/**
 * Prompt-based Lead Factory CRM actions from inventory and detail rows.
 */
import { labelize } from "./dashboard-html.js";
import { authenticatedFetch, getDashboardOperator } from "./dashboard-auth.js";
import type { DashboardActionHost } from "./dashboard-referenced-deed-actions.js";

export async function submitLeadAction(
  leadId: string,
  action: string,
  statusElement: HTMLElement,
  actions: DashboardActionHost
): Promise<void> {
  const endpointByAction: Record<string, string> = {
    assign: `/dashboard/lead-factory/leads/${encodeURIComponent(leadId)}/assign`,
    merge: `/dashboard/lead-factory/leads/${encodeURIComponent(leadId)}/merge`,
    unmerge: `/dashboard/lead-factory/leads/${encodeURIComponent(leadId)}/unmerge`,
    split: `/dashboard/lead-factory/leads/${encodeURIComponent(leadId)}/split`,
    campaign: `/dashboard/lead-factory/leads/${encodeURIComponent(leadId)}/campaign`,
    enrich: `/dashboard/lead-factory/leads/${encodeURIComponent(leadId)}/mark-needs-enrichment`,
    offer: `/dashboard/lead-factory/leads/${encodeURIComponent(leadId)}/offer`,
    "offer-document": `/dashboard/lead-factory/leads/${encodeURIComponent(leadId)}/offer-document`,
    "offer-sent": `/dashboard/lead-factory/leads/${encodeURIComponent(leadId)}/offer-status`,
    "contract-checklist": `/dashboard/lead-factory/leads/${encodeURIComponent(leadId)}/contract-checklist`,
    "contract-packet": `/dashboard/lead-factory/leads/${encodeURIComponent(leadId)}/contract-packet`,
    "title-handoff": `/dashboard/lead-factory/leads/${encodeURIComponent(leadId)}/title-handoff`,
    "title-clear": `/dashboard/lead-factory/leads/${encodeURIComponent(leadId)}/title-status`,
    "title-issue": `/dashboard/lead-factory/leads/${encodeURIComponent(leadId)}/title-status`,
    "closing-schedule": `/dashboard/lead-factory/leads/${encodeURIComponent(leadId)}/closing-schedule`,
    "closing-complete": `/dashboard/lead-factory/leads/${encodeURIComponent(leadId)}/closing-status`,
    "closing-artifact": `/dashboard/lead-factory/leads/${encodeURIComponent(leadId)}/closing-artifact`,
    "portfolio-handoff": `/dashboard/lead-factory/leads/${encodeURIComponent(leadId)}/portfolio-handoff`,
    "asset-onboarding": `/dashboard/lead-factory/leads/${encodeURIComponent(leadId)}/asset-onboarding`,
    "disposition-review": `/dashboard/lead-factory/leads/${encodeURIComponent(leadId)}/disposition-review`,
    "disposition-disposed": `/dashboard/lead-factory/leads/${encodeURIComponent(leadId)}/disposition-status`,
    "finance-record": `/dashboard/lead-factory/leads/${encodeURIComponent(leadId)}/finance-record`,
    "finance-artifact": `/dashboard/lead-factory/leads/${encodeURIComponent(leadId)}/finance-artifact`,
    "finance-reconciliation": `/dashboard/lead-factory/leads/${encodeURIComponent(leadId)}/finance-reconciliation`,
    ready: `/dashboard/lead-factory/leads/${encodeURIComponent(leadId)}/mark-ready`,
    pipeline: `/dashboard/lead-factory/leads/${encodeURIComponent(leadId)}/pipeline`,
    suppress: `/dashboard/lead-factory/leads/${encodeURIComponent(leadId)}/suppress`
  };
  const payloadByAction: Record<string, Record<string, string>> = {
    assign: { assignedTo: getDashboardOperator() || "unassigned" },
    merge: { targetLeadId: "", note: "Dashboard merged duplicate lead." },
    unmerge: { targetLeadId: "", note: "Dashboard reversed a duplicate merge for review." },
    split: { sourceDocumentIds: "", note: "Dashboard split source documents into a separate lead for identity review." },
    campaign: { template: "multichannel_21_day", note: "Dashboard generated a campaign template." },
    enrich: { note: "Dashboard routed lead to enrichment." },
    offer: { strategy: "cash_as_is", valueEstimate: "", offerAmount: "", note: "Dashboard generated an acquisition offer package." },
    "offer-document": { note: "Dashboard generated an acquisition offer document." },
    "offer-sent": { status: "sent", note: "Dashboard marked the acquisition offer sent." },
    "contract-checklist": { assignedTo: getDashboardOperator() || "unassigned", note: "Dashboard created an under-contract checklist." },
    "contract-packet": { note: "Dashboard generated an acquisition contract packet." },
    "title-handoff": { vendorName: "", assignedTo: getDashboardOperator() || "unassigned", note: "Dashboard ordered title review." },
    "title-clear": { status: "clear", note: "Dashboard marked title clear for closing coordination." },
    "title-issue": { status: "issue_found", issueReason: "other_typed_review", note: "Dashboard flagged a title issue for review." },
    "closing-schedule": { closingAt: "", closingLocation: "", assignedTo: getDashboardOperator() || "unassigned", note: "Dashboard scheduled closing for a clear-title lead." },
    "closing-complete": { status: "completed", note: "Dashboard marked acquisition closing completed." },
    "closing-artifact": {
      artifactKind: "settlement_statement",
      title: "",
      artifactText: "",
      note: ""
    },
    "portfolio-handoff": { status: "handoff_ready", note: "Dashboard created owned property handoff." },
    "asset-onboarding": { assignedTo: getDashboardOperator() || "unassigned", note: "Dashboard started owned property onboarding." },
    "disposition-review": {
      strategy: "resale",
      status: "strategy_selected",
      assignedTo: getDashboardOperator() || "unassigned",
      note: "Dashboard recorded owned property disposition strategy."
    },
    "disposition-disposed": {
      status: "disposed",
      assignedTo: getDashboardOperator() || "unassigned",
      note: "Dashboard marked owned property disposition completed."
    },
    "finance-record": {
      salePrice: "",
      saleClosedAt: "",
      repairExpenses: "",
      holdingExpenses: "",
      sellingExpenses: "",
      otherExpenses: "",
      note: ""
    },
    "finance-artifact": {
      artifactKind: "sale_settlement_statement",
      title: "",
      artifactText: "",
      note: ""
    },
    "finance-reconciliation": {
      reconciliationSource: "bank_deposit",
      expectedAmount: "",
      observedAmount: "",
      toleranceAmount: "",
      reference: "",
      note: ""
    },
    ready: { contactReadiness: "ready_for_letter", note: "Dashboard marked lead ready for letter contact." },
    pipeline: { stage: "interested", assignedTo: getDashboardOperator() || "unassigned", note: "Dashboard moved lead into acquisition pipeline." },
    suppress: { reason: "other_typed_review", note: "Dashboard suppression pending review detail." }
  };
  const url = endpointByAction[action];
  if (!url) return;
  const payload = { ...payloadByAction[action] };
  if (action === "merge") {
    const targetLeadId = window.prompt(`Merge ${leadId} into surviving lead ID:`);
    if (!targetLeadId?.trim()) {
      statusElement.textContent = "Merge cancelled; no target lead ID entered.";
      return;
    }
    payload.targetLeadId = targetLeadId.trim();
  }
  if (action === "unmerge") {
    const targetLeadId = window.prompt(`Reverse merge for ${leadId}; surviving target lead ID:`);
    if (!targetLeadId?.trim()) {
      statusElement.textContent = "Unmerge cancelled; no target lead ID entered.";
      return;
    }
    payload.targetLeadId = targetLeadId.trim();
  }
  if (action === "split") {
    const sourceDocumentIds = window.prompt(`Source document IDs to split from ${leadId} (comma separated):`);
    if (!sourceDocumentIds?.trim()) {
      statusElement.textContent = "Split cancelled; no source document IDs entered.";
      return;
    }
    payload.sourceDocumentIds = sourceDocumentIds.trim();
  }
  if (action === "offer") {
    const valueEstimate = window.prompt(`Value estimate for ${leadId}:`, payload.valueEstimate || "");
    if (!valueEstimate?.trim()) {
      statusElement.textContent = "Offer cancelled; no value estimate entered.";
      return;
    }
    const offerAmount = window.prompt(`Offer amount for ${leadId}:`, payload.offerAmount || "");
    if (!offerAmount?.trim()) {
      statusElement.textContent = "Offer cancelled; no offer amount entered.";
      return;
    }
    payload.valueEstimate = valueEstimate.trim();
    payload.offerAmount = offerAmount.trim();
  }
  if (action === "title-handoff") {
    const vendorName = window.prompt(`Title vendor for ${leadId}:`, payload.vendorName || "");
    if (!vendorName?.trim()) {
      statusElement.textContent = "Title handoff cancelled; no title vendor entered.";
      return;
    }
    payload.vendorName = vendorName.trim();
  }
  if (action === "closing-schedule") {
    const closingAt = window.prompt(`Closing date/time for ${leadId}:`, payload.closingAt || "");
    if (!closingAt?.trim()) {
      statusElement.textContent = "Closing schedule cancelled; no closing date entered.";
      return;
    }
    payload.closingAt = closingAt.trim();
  }
  statusElement.textContent = `${labelize(action)} ${leadId}...`;
  try {
    const response = await authenticatedFetch(url, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(payload)
    });
    const body = (await response.json()) as { error?: string };
    if (!response.ok) throw new Error(body.error ?? `Request failed with ${response.status}`);
    statusElement.textContent = `${leadId}: ${labelize(action)} recorded. Refreshing...`;
    setTimeout(() => actions.refresh(), 650);
  } catch (error) {
    statusElement.textContent = error instanceof Error ? error.message : "Lead action failed.";
  }
}
